// Redux Utils
import { useSelector, useDispatch } from "react-redux";

// React Utils
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Pacckages Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";

// Componentes
import AmbassadorTab from "./AmbassadorTab";
import EventDescription from "./EventDescription";
import EventLocation from "./EventLocation";
import EventTags from "./EventTags";
import Modal from "@/components/Modals/Modal";
import { actualizarUsoEmbajadoresEvento, getActiveAmbassadorsForEvent, actualizarEmbajadoresConLink } from "@/features/evento/eventoThunks";
import { getAllEmbajadores } from "@/features/embajador/embajadorThunks";
import { resetEmbajadores } from "@/features/embajador/embajadorSlice";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/components/ui/Toast";

// Assets
import noUserImage from "@/assets/avatars/avatar-generic.webp"

// importar constante con el listado de categorías válidas
import { CATEGORIAS_EVENTO } from "@/constants/categoriasEvento";

export default function CardAmbassadors({
  pendingAmbassadors = [],
  allAmbassadors = [],
  initialSelectedAmbassadors = [],
  onSave,
  infoEvento = {},
  infoEmpresa = {},
}) {
  // TODO: Retomar en la versión 1.5
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedAmbassadors, setSelectedAmbassadors] = useState(
    initialSelectedAmbassadors,
  );
  const [previewAmbassadors, setPreviewAmbassadors] = useState(
    initialSelectedAmbassadors,
  );
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedUso, setSelectedUso] = useState(null);
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para búsqueda y paginación de embajadores
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  
  // Toast notifications
  const { success, error, info, warning } = useToast();
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Obtener embajadores activos del evento desde Redux
  const { activeAmbassadors, isLoadingEvento } = useSelector((state) => state.evento);
  
  // Obtener lista completa de embajadores desde Redux
  const { 
    allEmbassadors: loadedAmbassadors, 
    isLoadingAll, 
    hasNextPage,
    currentPage 
  } = useSelector((state) => state.embajador);

  // Normalizar useAmbassador a número para evitar problemas de tipo
  const usoEvento = Number(infoEvento?.useAmbassador ?? 0);

  // Cargar embajadores activos cuando el evento usa modo exclusivo (useAmbassador === 2)
  useEffect(() => {
    if (usoEvento === 2 && infoEvento?.uid) {
      dispatch(getActiveAmbassadorsForEvent({ uidEvento: infoEvento.uid }));
    }
  }, [usoEvento, infoEvento?.uid, dispatch]);

  // Sincronizar activeAmbassadors con selectedAmbassadors y previewAmbassadors
  useEffect(() => {
    if (usoEvento === 2 && activeAmbassadors && activeAmbassadors.length > 0) {
      console.log('🔄 Sincronizando activeAmbassadors:', activeAmbassadors);
      
      // Transformar el formato del backend al formato esperado por los componentes
      const formattedAmbassadors = activeAmbassadors.map((emb) => ({
        id: emb.embajadorId,
        usuarioId: emb.usuarioId,
        nombre: `${emb.nombre} ${emb.apellido}`,
        name: `${emb.nombre} ${emb.apellido}`,
        img: emb.imgPerfil,
        avatar: emb.imgPerfil,
        instagram: emb.instagram,
        facebook: emb.facebook,
        tiktok: emb.tiktok,
        referralLink: emb.referralLink,
      }));

      console.log('✅ Embajadores formateados:', formattedAmbassadors);
      setSelectedAmbassadors(formattedAmbassadors);
      setPreviewAmbassadors(formattedAmbassadors);
    }
  }, [activeAmbassadors, usoEvento]);

  // Solo actualizar desde initialSelectedAmbassadors si NO estamos en modo 2 o si NO hay activeAmbassadors
  useEffect(() => {
    if (initialSelectedAmbassadors && initialSelectedAmbassadors.length > 0) {
      // No sobrescribir si estamos en modo 2 y ya tenemos activeAmbassadors cargados
      if (usoEvento === 2 && activeAmbassadors && activeAmbassadors.length > 0) {
        console.log('⏭️ Saltando actualización de initialSelectedAmbassadors porque hay activeAmbassadors');
        return;
      }
      
      console.log('📥 Actualizando desde initialSelectedAmbassadors:', initialSelectedAmbassadors);
      setSelectedAmbassadors(initialSelectedAmbassadors);
      setPreviewAmbassadors(initialSelectedAmbassadors);
    }
  }, [initialSelectedAmbassadors, usoEvento, activeAmbassadors]);

  // Cargar embajadores cuando se entra en modo edición
  useEffect(() => {
    if (isEditMode) {
      console.log('🔄 Cargando embajadores para modo edición...');
      dispatch(resetEmbajadores());
      dispatch(getAllEmbajadores({
        enabled: 1,
        page: 1,
        limit: 20,
        camposResponse: 'id,nombre,apellido,imgPerfil',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }));
    }
  }, [isEditMode, dispatch]);

  // Recargar cuando cambia la búsqueda
  useEffect(() => {
    if (isEditMode) {
      console.log('🔍 Búsqueda actualizada:', debouncedSearch);
      dispatch(resetEmbajadores());
      dispatch(getAllEmbajadores({
        search: debouncedSearch || undefined,
        enabled: 1,
        page: 1,
        limit: 20,
        camposResponse: 'id,nombre,apellido,imgPerfil',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }));
    }
  }, [debouncedSearch, isEditMode, dispatch]);

  // Infinite Scroll con Intersection Observer
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasNextPage && !isLoadingAll && isEditMode) {
      console.log('📄 Cargando más embajadores...');
      dispatch(getAllEmbajadores({
        search: debouncedSearch || undefined,
        enabled: 1,
        page: currentPage + 1,
        limit: 20,
        camposResponse: 'id,nombre,apellido,imgPerfil',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }));
    }
  }, [hasNextPage, isLoadingAll, isEditMode, currentPage, debouncedSearch, dispatch]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    });
    
    observerRef.current = observer;
    const currentLoadingRef = loadingRef.current;
    
    if (currentLoadingRef) {
      observer.observe(currentLoadingRef);
    }
    
    return () => {
      if (currentLoadingRef) {
        observer.unobserve(currentLoadingRef);
      }
    };
  }, [handleObserver]);
  
  const handleToggleAmbassador = (ambassador) => {
    setSelectedAmbassadors((prev) => {
      const exists = prev.some((a) => a.id === ambassador.id);
      if (exists) {
        return prev.filter((a) => a.id !== ambassador.id);
      } else {
        return [...prev, ambassador];
      }
    });
  };

  // TODO: Retomar en la versión 1.5
  const handleSave = async () => {
    // Validar que hay un evento
    if (!infoEvento?.uid) {
      error('❌ No se puede guardar: evento no identificado', { duration: 5000 });
      return;
    }

    // Extraer IDs de embajadores seleccionados (solo embajador.id)
    const embajadoresIds = selectedAmbassadors.map(amb => amb.id);

    console.log('💾 Guardando selección de embajadores:', {
      eventoUid: infoEvento.uid,
      embajadoresIds,
      total: embajadoresIds.length
    });

    // Mostrar loading
    setIsSaving(true);
    info(`📄 Actualizando ${embajadoresIds.length} embajador${embajadoresIds.length !== 1 ? 'es' : ''}...`, {
      duration: 2000
    });

    try {
      // Dispatch thunk para actualizar embajadores
      const result = await dispatch(
        actualizarEmbajadoresConLink({
          uidEvento: infoEvento.uid,
          embajadoresIds
        })
      );

      if (actualizarEmbajadoresConLink.fulfilled.match(result)) {
        // Éxito: mostrar resumen de cambios
        const summary = result.payload.summary;
        success(
          `✅ Embajadores actualizados exitosamente. ${
            summary.activated > 0 ? `Activados: ${summary.activated}. ` : ''
          }${
            summary.deactivated > 0 ? `Desactivados: ${summary.deactivated}. ` : ''
          }Total activos: ${summary.total}`,
          { duration: 6000 }
        );

        // Re-fetch para garantizar consistencia
        await dispatch(getActiveAmbassadorsForEvent({ uidEvento: infoEvento.uid }));

        // Actualizar estados locales y salir del modo edición
        setPreviewAmbassadors([...selectedAmbassadors]);
        setIsEditMode(false);

        // Callback opcional
        if (onSave) {
          onSave(selectedAmbassadors);
        }
      } else {
        // Error del backend
        throw new Error(result.payload || 'Error al actualizar embajadores');
      }
    } catch (err) {
      console.error('Error al guardar embajadores:', err);

      // Mostrar error específico
      const errorMessage = err.message.includes('autenticación')
        ? '🔐 Debes iniciar sesión para actualizar embajadores'
        : err.message.includes('permisos')
        ? '🚫 No tienes permisos para actualizar este evento'
        : err.message.includes('no encontrado')
        ? '🔍 Evento no encontrado'
        : err.message.includes('no trabaja con embajadores')
        ? '⚠️ Este evento no permite embajadores'
        : `❌ Error al guardar: ${err.message}`;

      error(errorMessage, { duration: 7000 });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    setSelectedAmbassadors([...previewAmbassadors]);
    setIsEditMode(false);
  };

  // Información de ejemplo ya no es necesaria
  const initialDataLocation = {
    direccion: infoEvento.direccion || "",
    ubicacion: infoEvento.ubicacion || "",
    showMapButton: true,
  };

  // TODO: Retomar en la versión 1.5
  const initialDataTags = CATEGORIAS_EVENTO;

  return (
    <div className="relative bg-aw-bluedark-95 rounded-2xl sm:p-6 flex flex-col items-center justify-between w-full min-h-[60dvh] xl:min-h-[80dvh] max-w-2xl">
      {/* TODO: Retomar en la versión 1.5 */}
      <div>
        {/* -*- Edit/Preview Toggle Button -*- */}
        {isEditMode ? (
          <></>
        ) : usoEvento === 2 ? (
          <Motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsEditMode(!isEditMode)}
            className="absolute top-4 right-4 z-50 px-4 py-2 rounded-full bg-aw-green/85 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2"
          >
            <FontAwesomeIcon
              icon={isEditMode ? "fa-solid fa-eye" : "fa-solid fa-edit"}
            />
            {isEditMode ? "Vista Previa" : "Editar"}
          </Motion.button>
        ) : (
          <></>
        )}

        <AnimatePresence mode="wait">
          {isEditMode ? (
            <Motion.div
              key="edit-mode"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full pt-4 space-y-6"
            >
              <div className="text-white font-bold text-xl text-center mb-4">
                Seleccionar Embajadores
              </div>

              {/* -*- Tab Navigation -*- */}
              <div className="flex gap-2 border-b border-gray-600">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`flex-1 py-3 px-4 font-medium transition duration-300 relative ${
                    activeTab === "all"
                      ? "text-aw-green"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <FontAwesomeIcon icon="fa-solid fa-users" className="mr-2" />
                  Todos los Embajadores
                  {loadedAmbassadors.length > 0 && (
                    <span className="ml-2 bg-gray-600 text-white text-xs px-2 py-1 rounded-full">
                      {loadedAmbassadors.length}
                    </span>
                  )}
                  {activeTab === "all" && (
                    <Motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-aw-green"
                    />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("selected")}
                  className={`flex-1 py-3 px-4 font-medium transition duration-300 relative ${
                    activeTab === "selected"
                      ? "text-aw-green"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <FontAwesomeIcon icon="fa-solid fa-users" className="mr-2" />
                  Embajadores Seleccionados
                  {selectedAmbassadors.length > 0 && (
                    <span className="ml-2 bg-gray-600 text-white text-xs px-2 py-1 rounded-full">
                      {selectedAmbassadors.length}
                    </span>
                  )}
                  {activeTab === "selected" && (
                    <Motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-aw-green"
                    />
                  )}
                </button>
                {/* <button
                  onClick={() => setActiveTab("pending")}
                  className={`flex-1 py-3 px-4 font-medium transition duration-300 relative ${
                    activeTab === "pending"
                      ? "text-aw-green"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <FontAwesomeIcon icon="fa-solid fa-clock" className="mr-2" />
                  Solicitudes Pendientes
                  {pendingAmbassadors.length > 0 && (
                    <span className="ml-2 bg-aw-green text-aw-bluedark text-xs px-2 py-1 rounded-full">
                      {pendingAmbassadors.length}
                    </span>
                  )}
                  {activeTab === "pending" && (
                    <Motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-aw-green"
                    />
                  )}
                </button> */}
              </div>

              {/* -*- Tab Content -*- */}
              <AnimatePresence mode="wait">
                {activeTab === "pending" ? (
                  <Motion.div
                    key="pending-tab"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {pendingAmbassadors.length > 0 ? (
                      <AmbassadorTab
                        ambassadors={pendingAmbassadors}
                        selectedAmbassadors={selectedAmbassadors}
                        onToggle={handleToggleAmbassador}
                      />
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <FontAwesomeIcon
                          icon="fa-solid fa-inbox"
                          className="text-5xl mb-3"
                        />
                        <p>No hay solicitudes pendientes</p>
                      </div>
                    )}
                  </Motion.div>
                ) :
                activeTab === "selected" ? (
                  <Motion.div
                    key="selected-tab" 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {selectedAmbassadors.length > 0 ? (
                      <AmbassadorTab
                        ambassadors={selectedAmbassadors}
                        selectedAmbassadors={selectedAmbassadors}
                        onToggle={handleToggleAmbassador}
                      />
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <FontAwesomeIcon
                          icon="fa-solid fa-user-check"
                          className="text-5xl mb-3"
                        />
                        <p>No hay embajadores seleccionados</p>
                      </div>
                    )}
                  </Motion.div>
                ) :(
                  <Motion.div
                    key="all-tab"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Campo de búsqueda */}
                    <div className="mb-4 relative">
                      <FontAwesomeIcon
                        icon="fa-solid fa-search"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar embajadores por nombre, apellido o ciudad..."
                        className="w-full pl-10 pr-4 py-2 bg-aw-bluedark border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
                      />
                    </div>

                    {/* Mostrar embajadores cargados, filtrando los ya seleccionados */}
                    {loadedAmbassadors.length > 0 ? (
                      <>
                        <AmbassadorTab
                          ambassadors={loadedAmbassadors.map(emb => ({
                            id: emb.id,
                            nombre: `${emb.nombre} ${emb.apellido}`,
                            name: `${emb.nombre} ${emb.apellido}`,
                            img: emb.imgPerfil,
                            avatar: emb.imgPerfil
                          }))}
                          selectedAmbassadors={selectedAmbassadors}
                          onToggle={handleToggleAmbassador}
                          hideSearch={true}
                        />
                        
                        {/* Indicador de carga para infinite scroll */}
                        {hasNextPage && (
                          <div ref={loadingRef} className="py-4 text-center">
                            <FontAwesomeIcon
                              icon="fa-solid fa-spinner"
                              className="text-aw-green text-2xl animate-spin"
                            />
                            <p className="text-gray-400 mt-2">Cargando más embajadores...</p>
                          </div>
                        )}
                        
                        {!hasNextPage && loadedAmbassadors.length > 0 && (
                          <div className="py-4 text-center text-gray-500 text-sm">
                            No hay más embajadores para mostrar
                          </div>
                        )}
                      </>
                    ) : isLoadingAll ? (
                      <div className="text-center py-12 text-gray-400">
                        <FontAwesomeIcon
                          icon="fa-solid fa-spinner"
                          className="text-5xl mb-3 animate-spin"
                        />
                        <p>Cargando embajadores...</p>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <FontAwesomeIcon
                          icon="fa-solid fa-user-slash"
                          className="text-5xl mb-3"
                        />
                        <p>No hay embajadores disponibles</p>
                      </div>
                    )}
                  </Motion.div>
                )}
              </AnimatePresence>

              {/* -*- Save and Cancel Buttons -*- */}
              <div className="flex gap-3 pt-4">
                <Motion.button
                  whileHover={{ scale: isSaving ? 1 : 1.02 }}
                  whileTap={{ scale: isSaving ? 1 : 0.98 }}
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex-1 py-3 ${
                    isSaving 
                      ? 'bg-gray-500 cursor-not-allowed' 
                      : 'bg-aw-green/85 hover:bg-aw-green'
                  } text-aw-bluedark font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2`}
                >
                  {isSaving ? (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-save" />
                      Guardar Selección
                    </>
                  )}
                </Motion.button>
                <Motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCancel}
                  disabled={isSaving}
                  className={`flex-1 py-3 ${
                    isSaving 
                      ? 'bg-gray-700 cursor-not-allowed' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  } text-white font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2`}
                >
                  <FontAwesomeIcon icon="fa-solid fa-times" />
                  Cancelar
                </Motion.button>
              </div>
            </Motion.div>
          ) : (
            <Motion.div
              key="preview-mode"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              {usoEvento === 0 || usoEvento === 1 ? (
                <div className="text-center py-8 text-gray-400">
                  {usoEvento === 0 ? (
                    <FontAwesomeIcon
                      icon="fa-solid fa-user-slash"
                      className="text-4xl mb-2"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon="fa-solid fa-users"
                      className="text-4xl mb-2"
                    />
                  )}
                  <p>
                    {usoEvento === 0
                      ? "Este evento no utiliza embajadores"
                      : "Este evento utiliza todos los embajadores de AppWhoop"}
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log(
                        "Botón clickeado - usoEvento:",
                        usoEvento,
                        "showUpdateModal antes:",
                        showUpdateModal,
                      );
                      setSelectedUso(null);
                      setSelectedIncentive(null);
                      setShowUpdateModal(true);
                      console.log("setShowUpdateModal(true) ejecutado");
                    }}
                    className="mt-4 mx-auto px-4 py-2 bg-aw-green text-aw-bluedark font-bold rounded-lg hover:scale-95 transition duration-300 flex items-center justify-center gap-2"
                  >
                    Actualizar configuración
                  </button>
                </div>
              ) : usoEvento === 2 ? (
                <>
                  <div className="text-white font-bold text-lg mb-4 text-center">
                    Staff Oficial de Embajadores
                  </div>

                  {isLoadingEvento ? (
                    <div className="text-center py-8 text-gray-400">
                      <FontAwesomeIcon
                        icon="fa-solid fa-spinner"
                        className="text-4xl mb-2 animate-spin"
                      />
                      <p>Cargando embajadores...</p>
                    </div>
                  ) : activeAmbassadors && activeAmbassadors.length > 0 ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex gap-3 overflow-x-auto flex-wrap justify-center scrollbar-hide px-4 py-2">
                        {activeAmbassadors.map((emb, i) => (
                          <Motion.div
                            key={emb.usuarioId || i}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex flex-col items-center min-w-[80px]"
                          >
                            <img
                              src={emb.imgPerfil || noUserImage}
                              alt={`${emb.nombre} ${emb.apellido}`}
                              className="w-16 h-16 rounded-full object-cover border-2 bg-aw-bluegray border-aw-green"
                            />
                            <span className="w-20 text-xs text-white text-center mt-2 font-semibold line-clamp-2">
                              {emb.nombre} {emb.apellido}
                            </span>
                          </Motion.div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <FontAwesomeIcon
                        icon="fa-solid fa-user-plus"
                        className="text-4xl mb-2"
                      />
                      <p>No hay embajadores activos</p>
                      <p className="text-sm mt-1">
                        Los embajadores aparecerán aquí cuando tengan un link activo
                      </p>
                    </div>
                  )}
                </>
              ) : null}
            </Motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="w-full mt-6">
        <EventDescription
          initialDescription={infoEvento.descripcion || ""}
          onSave={(desc) => console.log("Descripción guardada:", desc)}
          eventIsPublished={infoEvento?.published}
        />
      </div>
      <div className="w-full mt-6">
        <EventLocation
          initialData={initialDataLocation}
          onSave={(loc) => console.log("Ubicación guardada:", loc)}
          eventIsPublished={infoEvento?.published}
        />
      </div>
      {/* TODO: Retomar en la versión 1.5 */}
      <div className="w-full mt-6">
        {/* availableTags = [], initialSelectedTags = [], onSave */}
        <EventTags
          availableTags={initialDataTags}
          initialSelectedTags={
            infoEvento.categoriaEvento ? [infoEvento.categoriaEvento] : []
          }
          // onSave={(tags) => console.log("Etiquetas guardadas:", tags)}
          eventIsPublished={infoEvento?.published}
        />
      </div>

      {/* Modal para actualizar uso e incentivo */}
      {showUpdateModal && (
        <Modal
          isOpen={showUpdateModal}
          onClose={() => {
            console.log("Cerrando modal de actualización");
            setShowUpdateModal(false);
          }}
          large="max-w-4xl"
        >
          <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin overflow-y-auto">
            <h3 className="text-lg font-bold text-white mb-4">
              Actualizar Configuración de Embajadores
            </h3>
            {console.log(
              "Modal renderizado - showUpdateModal:",
              showUpdateModal,
            )}
            <p className="text-sm text-gray-300 mb-4">
              Seleccione el nuevo modo de uso y el incentivo para los
              embajadores.
            </p>

            <div className="mb-4">
              <label className="text-white block">Nueva configuración</label>
              <p className="text-xs text-gray-300 mb-2">
                Seleccione si desea usar todos los embajadores de AppWhoop para
                este evento o hacer una selección exclusiva de embajadores.{" "}
                <span className="text-red-400">
                  Tome en cuenta que no podrá regresar a la configuración
                  anterior una vez realizada la actualización.
                </span>
              </p>
              <div className="flex gap-2">
                {usoEvento === 0 && (
                  <button
                    onClick={() => setSelectedUso(1)}
                    className={`px-3 py-2 rounded ${selectedUso === 1 ? "bg-aw-green text-aw-bluedark" : "bg-gray-700 text-white"}`}
                  >
                    Usar todos
                  </button>
                )}
                <button
                  onClick={() => setSelectedUso(2)}
                  className={`px-3 py-2 rounded ${selectedUso === 2 ? "bg-aw-green text-aw-bluedark" : "bg-gray-700 text-white"}`}
                >
                  Selección exclusiva{" "}
                  {!(infoEmpresa && infoEmpresa.planActivo) && (
                    <FontAwesomeIcon
                      icon="fa-solid fa-calendar-check"
                      className={`ml-1 ${selectedUso === 2 ? "text-aw-bluedark" : "text-aw-green"}`}
                    />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white block">Comisión</label>
              <p className="text-xs text-gray-300 mb-2">
                Seleccione el porcentaje de incentivo que ofrecerá a los
                embajadores por cada venta realizada a través de ellos.{" "}
                <span className="text-red-400">
                  Tome en cuenta que este dato no podrá ser modificado
                  posteriormente.
                </span>
              </p>
              <div className="flex gap-2 flex-wrap">
                {(() => {
                  // Generar opciones de 3, 5, 10, 15, 20 según reglas
                  const options = [3, 5, 10, 15, 20];
                  const currentIncentive = infoEvento.ambassadorIncentive || 0;
                  const isFromZero = usoEvento === 0;
                  let filtered = options.filter((o) => {
                    if (isFromZero) return true; // show all when previous incentive is 0
                    // if going from 1->2 allow current as option and higher
                    return o >= currentIncentive + 0;
                  });
                  // If current incentive is not in list and not zero and transitioning 1->2, include it
                  if (
                    !filtered.includes(currentIncentive) &&
                    currentIncentive > 0
                  ) {
                    filtered = [currentIncentive, ...filtered].sort(
                      (a, b) => a - b,
                    );
                  }
                  return filtered.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setSelectedIncentive(opt)}
                      className={`px-3 py-2 rounded ${selectedIncentive === opt ? "bg-aw-green text-aw-bluedark" : "bg-gray-700 text-white"}`}
                    >
                      {opt}%
                    </button>
                  ));
                })()}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancelar
              </button>
              <button
                disabled={isSubmittingUpdate}
                onClick={async () => {
                  // Validaciones
                  if (!selectedUso)
                    return alert("Seleccione una nueva configuración");
                  if (
                    !selectedIncentive &&
                    !(usoEvento === 0 && selectedIncentive === 0)
                  )
                    return alert("Seleccione la nueva comisión");

                  // Si la empresa no tiene plan y quiere seleccionar uso 2, mostrar modal de plan
                  if (selectedUso === 2 && !(infoEmpresa && infoEmpresa.planActivo)) {
                    setShowUpdateModal(false);
                    setShowPlanModal(true);
                    return;
                  }

                  setIsSubmittingUpdate(true);
                  try {
                    await dispatch(
                      actualizarUsoEmbajadoresEvento({
                        uidEvento: infoEvento.uid,
                        usoEmbajadores: selectedUso,
                        incentivo: selectedIncentive,
                      }),
                    ).unwrap();
                    setShowUpdateModal(false);
                  } catch (err) {
                    alert(err?.message || err || "Error actualizando");
                  } finally {
                    setIsSubmittingUpdate(false);
                  }
                }}
                className="px-4 py-2 bg-aw-green text-aw-bluedark rounded font-bold"
              >
                Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal informativo de plan */}
      {showPlanModal && (
        <Modal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          large="max-w-2xl"
        >
          <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin overflow-y-auto text-center">
            <FontAwesomeIcon
              icon="fa-solid fa-info-circle"
              className="text-5xl text-aw-green mb-4 mx-auto"
            />
            <h3 className="text-lg font-bold text-white mb-4">
              Necesitas un plan
            </h3>
            <p className="text-sm text-gray-300">
              Para usar la{" "}
              <b className="font-semibold text-aw-green">
                "Selección exclusiva de embajadores"
              </b>{" "}
              necesitas adquirir un plan.
            </p>
            <div className="flex justify-center gap-3 mt-6">
              {/* <button onClick={() => setShowPlanModal(false)} className="px-4 py-2 bg-gray-600 text-white rounded">Cerrar</button> */}
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  navigate(`/empresa/${infoEmpresa.uid}`);
                }}
                className="px-4 py-2 bg-aw-green text-aw-bluedark rounded font-bold"
              >
                Ir a Suscripción
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
